import React, { Fragment } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { ApolloProvider } from 'react-apollo';
import { IntlProvider, addLocaleData } from 'react-intl';
import en from 'react-intl/locale-data/en';
import fr from 'react-intl/locale-data/fr';

import localeData from './i18n/data.json';
import { AuthProvider, AuthRoles } from './auth';
import { apolloClient } from './api';
import * as Pages from './pages';
import { Routes, PrivateRoute } from './routing';
import { AnalyticsProvider, AuthAnalyticsInterceptor } from './analytics';
import { TopNavigation } from './components';
import { ScrollManager } from './components/Wrappers';
import { ConfigureOneProvider } from './contexts';

addLocaleData([...en, ...fr]);
// Define user's language. Different browsers have the user locale defined
// on different fields on the `navigator` object, so we make sure to account
// for these different by checking all of them
const language = navigator.languages
  ? navigator.languages[0]
  : navigator.language;
// Split locales with a region code
const languageWithoutRegionCode = language.toLowerCase().split(/[_-]+/)[0];
// Use only `en`
let messages = localeData.en;

// Enable localization only on local dev
if (process.env.NODE_ENV === 'development') {
  // Try full locale, try locale without region code, fallback to 'en'
  messages =
    localeData[languageWithoutRegionCode] ||
    localeData[language] ||
    localeData.en;
}

const App = () => (
  <AnalyticsProvider>
    <IntlProvider
      locale={language}
      messages={messages}
      textComponent={Fragment}
    >
      <ApolloProvider client={apolloClient}>
        <AuthProvider>
          <ConfigureOneProvider>
            <AuthAnalyticsInterceptor />
            <Router>
              <ScrollManager>
                <TopNavigation />

                <Switch>
                  <PrivateRoute
                    exact
                    path={'/index.html'}
                    component={Pages.Landing}
                  />
                  <PrivateRoute
                    exact
                    path={Routes.landing}
                    component={Pages.Landing}
                  />
                  <PrivateRoute
                    exact
                    path={Routes.rep}
                    component={Pages.MyAGIRep}
                  />
                  <PrivateRoute
                    exact
                    path={Routes.pricing}
                    component={Pages.PricingHelper}
                    roles={[AuthRoles.Sales]}
                    gate={[AuthRoles.Dealer]}
                  />
                  <PrivateRoute
                    exact
                    path={Routes.manuals}
                    component={Pages.Manuals}
                  />
                  <PrivateRoute
                    exact
                    path={Routes.manuals + '/:id'}
                    component={Pages.Manuals}
                  />
                  <PrivateRoute
                    exact
                    path={Routes.brochures}
                    component={Pages.Brochures}
                  />
                  <PrivateRoute
                    exact
                    path={Routes.logos}
                    component={Pages.Logos}
                  />
                  <PrivateRoute
                    exact
                    path={Routes.priceLists}
                    component={Pages.PriceLists}
                  />
                  <PrivateRoute
                    exact
                    path={Routes.warrantyclaims}
                    component={Pages.WarrantyClaims}
                  />
                  <PrivateRoute
                    exact
                    path={Routes.warrantyclaims + '/:id'}
                    component={Pages.WarrantyClaimConfirmation}
                    gate={[AuthRoles.Sales]}
                  />
                  <PrivateRoute
                    exact
                    path={Routes.announcement + '/:id'}
                    component={Pages.Announcement}
                  />
                  <Route exact path={Routes.login} component={Pages.Login} />
                  <Route exact path={Routes.terms} component={Pages.Terms} />
                  <Route
                    exact
                    path={Routes.contact}
                    component={Pages.ContactUs}
                  />
                  <Route
                    exact
                    path={Routes.augerCalculator}
                    component={Pages.AugerCalculator}
                  />
                  <Route component={Pages.NotFound} />
                </Switch>
              </ScrollManager>
            </Router>
          </ConfigureOneProvider>
        </AuthProvider>
      </ApolloProvider>
    </IntlProvider>
  </AnalyticsProvider>
);

export default App;
